/**
 * OvertimeTypes API Client
 * Handles API calls for company overtime type CRUD operations.
 *
 * The backend now uses separate `costMultiplier` / `billMultiplier` columns,
 * matching the frontend OvertimeType shape directly.
 */

import { apiClient } from '@/services/apiClient';
import type { OvertimeTypeDto, CreateOvertimeTypeDto, UpdateOvertimeTypeDto } from '@/types/api';
import type { OvertimeType } from '@/types/timesheetSettings';

// ─── Mappers ────────────────────────────────────────────────────────────────

/**
 * Convert a backend OvertimeTypeDto to the frontend OvertimeType shape.
 */
export function fromOvertimeTypeDto(dto: OvertimeTypeDto): OvertimeType {
  return {
    id: String(dto.id),
    name: dto.name,
    costMultiplier: dto.costMultiplier ?? undefined,
    billMultiplier: dto.billMultiplier ?? undefined,
    badgeColour: dto.badgeColour,
    isDefault: dto.isDefault,
    active: dto.active,
    createdAt: new Date(dto.createdAt),
  };
}

// ─── Payload types ──────────────────────────────────────────────────────────

export interface CreateOvertimeTypePayload {
  name: string;
  costMultiplier?: number;
  billMultiplier?: number;
  badgeColour: string;
  isDefault: boolean;
}

export interface UpdateOvertimeTypePayload {
  name: string;
  costMultiplier?: number;
  billMultiplier?: number;
  badgeColour: string;
}

// ─── API functions ──────────────────────────────────────────────────────────

export async function getOvertimeTypes(
  companyId: string,
  includeInactive = false
): Promise<OvertimeType[]> {
  const dtos = await apiClient.get<OvertimeTypeDto[]>(
    `/companies/${companyId}/overtime-types?includeInactive=${includeInactive}`
  );
  return dtos.map(fromOvertimeTypeDto);
}

export async function createOvertimeType(
  companyId: string,
  payload: CreateOvertimeTypePayload
): Promise<OvertimeType> {
  const body: CreateOvertimeTypeDto = {
    name: payload.name,
    costMultiplier: payload.costMultiplier ?? null,
    billMultiplier: payload.billMultiplier ?? null,
    badgeColour: payload.badgeColour,
    isDefault: payload.isDefault,
  };
  const dto = await apiClient.post<OvertimeTypeDto>(`/companies/${companyId}/overtime-types`, body);
  return fromOvertimeTypeDto(dto);
}

export async function updateOvertimeType(
  companyId: string,
  id: string,
  payload: UpdateOvertimeTypePayload
): Promise<OvertimeType> {
  const body: UpdateOvertimeTypeDto = {
    name: payload.name,
    costMultiplier: payload.costMultiplier ?? null,
    billMultiplier: payload.billMultiplier ?? null,
    badgeColour: payload.badgeColour,
  };
  const dto = await apiClient.put<OvertimeTypeDto>(`/companies/${companyId}/overtime-types/${id}`, body);
  return fromOvertimeTypeDto(dto);
}

export async function deleteOvertimeType(
  companyId: string,
  id: string
): Promise<void> {
  await apiClient.delete<void>(`/companies/${companyId}/overtime-types/${id}`);
}

export async function setDefaultOvertimeType(
  companyId: string,
  id: string
): Promise<OvertimeType> {
  const dto = await apiClient.patch<OvertimeTypeDto>(
    `/companies/${companyId}/overtime-types/${id}/set-default`
  );
  return fromOvertimeTypeDto(dto);
}
